import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Dropdown, Input, Modal, Typography } from 'antd'
import { Icon } from '@iconify/react'
import { debounce } from 'lodash'
import co7LogoDark from '../../assets/svg/CO7_Logo_Dark.svg'
import { showActionNotification } from '../../components/notification'
import HeaderPrimaryButton from '../../components/HeaderPrimaryButton'
import { loginState } from '../login/login.slice'
import CollectionCard from './components/CollectionCard'
import CollectionCardSkeleton from './components/CollectionCardSkeleton'
import CollectionFormModal from './components/CollectionFormModal'
import ManageMembersModal from './components/ManageMembersModal'
import { CO7_STATUS_LABELS, isCo7CollectionArchived } from './co7.constants'
import {
    co7State,
    createCo7Collection,
    deleteCo7Collection,
    fetchCo7CollectionById,
    loadCo7Collections,
    updateCo7Collection,
} from './co7.slice'

import styles from './CO7Page.module.css'

const { Title, Text } = Typography

const STATUS_LABELS = CO7_STATUS_LABELS

const CO7Page = () => {
    const dispatch = useDispatch()
    const { userProfile } = useSelector(loginState)
    const { status, mutationStatus, error, collections, isNext, totalCount } =
        useSelector(co7State)

    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState(null)
    const [formOpen, setFormOpen] = useState(false)
    const [formMode, setFormMode] = useState('create')
    const [editingCollection, setEditingCollection] = useState(null)
    const [membersModalOpen, setMembersModalOpen] = useState(false)
    const [activeCollection, setActiveCollection] = useState(null)

    const currentUserId = userProfile?._id
    const isSuperAdmin = Boolean(userProfile?.isSuperAdmin)
    const isLoading = status === 'loading'
    const isLoadingMore = status === 'loadingMore'

    useEffect(() => {
        dispatch(loadCo7Collections({ reset: true, search: '' }))
    }, [dispatch])

    const debouncedSearch = useMemo(
        () =>
            debounce((value, nextStatusFilter) => {
                dispatch(
                    loadCo7Collections({
                        reset: true,
                        search: value,
                        statusFilter: nextStatusFilter,
                    })
                )
            }, 350),
        [dispatch]
    )

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch])

    const handleSearchChange = (value) => {
        setSearch(value)
        debouncedSearch(value, statusFilter)
    }

    const handleStatusFilterChange = useCallback(
        (nextStatus) => {
            setStatusFilter(nextStatus)
            dispatch(
                loadCo7Collections({
                    reset: true,
                    search,
                    statusFilter: nextStatus,
                })
            )
        },
        [dispatch, search]
    )

    const clearStatusFilter = useCallback(
        (e) => {
            e.preventDefault()
            e.stopPropagation()
            setStatusFilter(null)
            dispatch(
                loadCo7Collections({
                    reset: true,
                    search,
                    statusFilter: null,
                })
            )
        },
        [dispatch, search]
    )

    const statusMenu = useMemo(
        () => ({
            items: [
                { key: 'active', label: 'Active' },
                { key: 'archived', label: 'Archived' },
            ],
            onClick: ({ key, domEvent }) => {
                domEvent?.stopPropagation?.()
                handleStatusFilterChange(key)
            },
        }),
        [handleStatusFilterChange]
    )

    const filterTrigger = (label) => (
        <button type="button" className={styles.pillBtn}>
            <span className={styles.pillLabel}>{label}</span>
            <span className={styles.pillChevron}>
                <Icon icon="mdi:chevron-down" width={18} height={18} />
            </span>
        </button>
    )

    const hasActiveFilters = Boolean(search.trim() || statusFilter)

    const openCreate = () => {
        setFormMode('create')
        setEditingCollection(null)
        setFormOpen(true)
    }

    const openEdit = async (collection) => {
        if (isCo7CollectionArchived(collection)) {
            showActionNotification({
                type: 'warning',
                message: 'Unarchive this collection before editing it.',
            })
            return
        }
        setFormMode('edit')
        setEditingCollection(collection)
        setFormOpen(true)
        if (collection?._id) {
            const result = await dispatch(
                fetchCo7CollectionById(collection._id)
            )
            if (fetchCo7CollectionById.fulfilled.match(result)) {
                setEditingCollection((prev) => ({
                    ...prev,
                    ...result.payload,
                }))
            }
        }
    }

    const openMembers = (collection) => {
        setActiveCollection(collection)
        setMembersModalOpen(true)
    }

    const handleFormSubmit = async (values) => {
        if (formMode === 'create') {
            const result = await dispatch(
                createCo7Collection({
                    name: values.name,
                    description: values.description,
                })
            )
            if (createCo7Collection.fulfilled.match(result)) {
                showActionNotification({
                    type: 'success',
                    message: 'Collection created',
                })
                setFormOpen(false)
                dispatch(
                    loadCo7Collections({
                        reset: true,
                        search,
                        statusFilter,
                    })
                )
            } else {
                showActionNotification({
                    type: 'error',
                    message: result.payload || 'Failed to create collection',
                })
            }
            return
        }

        if (!editingCollection?._id) return
        if (isCo7CollectionArchived(editingCollection)) {
            showActionNotification({
                type: 'warning',
                message: 'Archived collections cannot be edited.',
            })
            return
        }
        const result = await dispatch(
            updateCo7Collection({
                id: editingCollection._id,
                name: values.name,
                description: values.description,
            })
        )
        if (updateCo7Collection.fulfilled.match(result)) {
            showActionNotification({
                type: 'success',
                message: 'Collection updated',
            })
            setFormOpen(false)
            if (activeCollection?._id === editingCollection._id) {
                setActiveCollection((prev) => ({
                    ...prev,
                    name: values.name,
                    description: values.description,
                }))
            }
        } else {
            showActionNotification({
                type: 'error',
                message: result.payload || 'Failed to update collection',
            })
        }
    }

    const handleArchiveCollection = useCallback(
        (collection) => {
            const isArchived = Boolean(collection?.isDeleted)

            Modal.confirm({
                title: isArchived
                    ? 'Unarchive collection?'
                    : 'Archive collection?',
                content: isArchived
                    ? `"${collection?.name}" will be restored to the active list.`
                    : `"${collection?.name}" will be archived and hidden from the active list.`,
                okText: isArchived ? 'Unarchive' : 'Archive',
                okButtonProps: isArchived ? {} : { danger: true },
                onOk: async () => {
                    const result = isArchived
                        ? await dispatch(
                              updateCo7Collection({
                                  id: collection._id,
                                  isDeleted: false,
                              })
                          )
                        : await dispatch(deleteCo7Collection(collection._id))

                    const succeeded = isArchived
                        ? updateCo7Collection.fulfilled.match(result)
                        : deleteCo7Collection.fulfilled.match(result)

                    if (succeeded) {
                        showActionNotification({
                            type: 'success',
                            message: isArchived
                                ? 'Collection unarchived'
                                : 'Collection archived',
                        })
                        if (activeCollection?._id === collection._id) {
                            setMembersModalOpen(false)
                            setActiveCollection(null)
                        }
                        dispatch(
                            loadCo7Collections({
                                reset: true,
                                search,
                                statusFilter,
                            })
                        )
                    } else {
                        showActionNotification({
                            type: 'error',
                            message:
                                result.payload ||
                                (isArchived
                                    ? 'Failed to unarchive collection'
                                    : 'Failed to archive collection'),
                        })
                    }
                },
            })
        },
        [dispatch, activeCollection?._id, search, statusFilter]
    )

    const activeCollectionLive = useMemo(() => {
        if (!activeCollection?._id) return null
        return (
            collections.find((c) => c._id === activeCollection._id) ||
            activeCollection
        )
    }, [collections, activeCollection])

    const loadMore = () => {
        if (!isNext || isLoadingMore || isLoading) return
        dispatch(loadCo7Collections({ reset: false, statusFilter }))
    }

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div className={styles.headerBrand}>
                    <img
                        src={co7LogoDark}
                        alt=""
                        className={styles.brandLogo}
                        aria-hidden
                    />
                    <div className={styles.headerBrandText}>
                        <Title level={3} className={styles.title}>
                            CO7
                        </Title>
                        <Text className={styles.subtitle}>
                            Shared collections - create, invite members, and
                            collaborate with viewer or editor roles.
                        </Text>
                    </div>
                </div>
                <div className={styles.headerControls}>
                    <div className={styles.searchWrap}>
                        <Input
                            allowClear
                            placeholder="Search collection"
                            className={styles.search}
                            prefix={
                                <Icon
                                    icon="mdi:magnify"
                                    width={18}
                                    height={18}
                                />
                            }
                            value={search}
                            onChange={(e) =>
                                handleSearchChange(e.target.value || '')
                            }
                        />
                    </div>
                    <div className={styles.filterTriggers}>
                        <Dropdown
                            menu={statusMenu}
                            trigger={['click']}
                            getPopupContainer={(n) =>
                                n?.parentElement ?? document.body
                            }
                        >
                            {statusFilter ? (
                                <div
                                    className={styles.statusFilterChip}
                                    role="group"
                                    aria-label={`Status filter: ${STATUS_LABELS[statusFilter]}`}
                                >
                                    <span
                                        className={styles.statusFilterChipLabel}
                                    >
                                        {STATUS_LABELS[statusFilter]}
                                    </span>
                                    <button
                                        type="button"
                                        className={styles.statusFilterChipClear}
                                        onClick={clearStatusFilter}
                                        aria-label="Clear status filter"
                                    >
                                        <Icon
                                            icon="mdi:close"
                                            width={16}
                                            height={16}
                                        />
                                    </button>
                                </div>
                            ) : (
                                filterTrigger('Status')
                            )}
                        </Dropdown>
                    </div>
                    {totalCount > 0 ? (
                        <span className={styles.countBadge}>
                            {totalCount} collection
                            {totalCount === 1 ? '' : 's'}
                        </span>
                    ) : null}
                    {collections?.length > 0 ? (
                        <HeaderPrimaryButton onClick={openCreate}>
                            New collection
                        </HeaderPrimaryButton>
                    ) : null}
                </div>
            </header>

            <div className={styles.content}>
                {status === 'error' ? (
                    <div className={styles.errorState}>
                        <div className={styles.errorIconBadge} aria-hidden>
                            <Icon
                                icon="mdi:cloud-alert-outline"
                                width={26}
                                height={26}
                                className={styles.errorIcon}
                            />
                        </div>
                        <p className={styles.errorTitle}>
                            Unable to load collections
                        </p>
                        <p className={styles.errorMessage}>
                            {error || 'Something went wrong. Please try again.'}
                        </p>
                        <button
                            type="button"
                            className={styles.errorRetryBtn}
                            onClick={() =>
                                dispatch(
                                    loadCo7Collections({
                                        reset: true,
                                        search,
                                        statusFilter,
                                    })
                                )
                            }
                        >
                            <Icon icon="mdi:refresh" width={16} />
                            Try again
                        </button>
                    </div>
                ) : isLoading && collections.length === 0 ? (
                    <div
                        className={styles.grid}
                        aria-busy="true"
                        aria-label="Loading collections"
                    >
                        {Array.from({ length: 4 }).map((_, index) => (
                            <CollectionCardSkeleton
                                key={`collection-skeleton-${index}`}
                                index={index}
                            />
                        ))}
                    </div>
                ) : collections?.length > 0 ? (
                    <>
                        <div className={styles.grid}>
                            {collections.map((collection) => (
                                <CollectionCard
                                    key={collection._id}
                                    collection={collection}
                                    currentUserId={currentUserId}
                                    isSuperAdmin={isSuperAdmin}
                                    onEdit={openEdit}
                                    onArchive={handleArchiveCollection}
                                    onManageMembers={openMembers}
                                />
                            ))}
                        </div>
                        {isNext ? (
                            <div className={styles.loadMoreWrap}>
                                <button
                                    type="button"
                                    className={styles.loadMoreBtn}
                                    onClick={loadMore}
                                    disabled={isLoadingMore}
                                >
                                    {isLoadingMore ? (
                                        <Icon
                                            icon="line-md:loading-loop"
                                            width={18}
                                        />
                                    ) : null}
                                    {isLoadingMore ? 'Loading…' : 'Load more'}
                                </button>
                            </div>
                        ) : null}
                    </>
                ) : (
                    <div className={styles.emptyState}>
                        <div className={styles.emptyCard}>
                            <div className={styles.emptyIconWrap} aria-hidden>
                                <Icon
                                    icon={
                                        hasActiveFilters
                                            ? 'mdi:magnify-close'
                                            : 'mdi:folder-multiple-outline'
                                    }
                                    width={26}
                                    height={26}
                                    className={styles.emptyIcon}
                                />
                            </div>
                            <Text className={styles.emptyTitle}>
                                {hasActiveFilters
                                    ? 'No collections match your filters'
                                    : 'No collections yet'}
                            </Text>
                            <Text className={styles.emptyText}>
                                {hasActiveFilters
                                    ? 'Try another keyword, change the status filter, or clear filters to browse all collections.'
                                    : 'Create a collection to organize shared content and invite members with viewer or editor roles.'}
                            </Text>
                            {!hasActiveFilters ? (
                                <button
                                    type="button"
                                    className={styles.emptyCreateBtn}
                                    onClick={openCreate}
                                >
                                    <Icon icon="mdi:plus" width={18} />
                                    Create your first collection
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className={styles.emptyClearBtn}
                                    onClick={() => {
                                        setStatusFilter(null)
                                        setSearch('')
                                        debouncedSearch.cancel()
                                        dispatch(
                                            loadCo7Collections({
                                                reset: true,
                                                search: '',
                                                statusFilter: null,
                                            })
                                        )
                                    }}
                                >
                                    Clear filters
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <CollectionFormModal
                open={formOpen}
                mode={formMode}
                initialValues={editingCollection}
                loading={mutationStatus === 'loading'}
                onCancel={() => setFormOpen(false)}
                onSubmit={handleFormSubmit}
            />

            <ManageMembersModal
                open={membersModalOpen}
                collection={activeCollectionLive}
                currentUserId={currentUserId}
                isSuperAdmin={isSuperAdmin}
                onClose={() => {
                    setMembersModalOpen(false)
                    setActiveCollection(null)
                }}
            />
        </div>
    )
}

export default CO7Page
